import React, { useState, useEffect, useRef } from 'react';
import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import {
  signUp, signIn, confirmSignUp, resendSignUpCode, resetPassword, confirmResetPassword,
} from 'aws-amplify/auth';
import { normalizeAuthInput } from '../auth/normalizeEmail';
import { Navigate, Link, useLocation } from 'react-router-dom';
import { detectInAppBrowser, androidChromeUrl, iosExternalBrowserUrl } from '../utils/inAppBrowser';
import { trackSignupError } from '../analytics/signup';
import { onRedirectFailure } from '../auth/redirectFailure';
import InAppSignUpEscape, { InAppSignUpNote } from '../components/InAppSignUpEscape';
import { rememberReturnTo, peekReturnTo, safeReturnPath } from '../utils/returnTo';
import '../App.css';

interface AuthPageProps {
  // No 'forgotPassword': that opens Cognito's own reset, which #926 turned into a dead end.
  initialState?: 'signIn' | 'signUp';
}

// Defined outside the component to avoid recreation on every render.
//
// Sign-in-wrap, not a checkbox. The tick box that used to live here was 16px square,
// wrapped mid-sentence, and — because validateCustomSignUp reported the unticked box as
// a validation error — the form opened with a red "You must agree..." message and a
// disabled Create Account button before the visitor had typed anything. Two people
// reached this page and left. (#803)
//
// A conspicuous notice beside the action is the standard pattern and is generally
// enforceable where it is legible and adjacent; terms merely linked in a page footer
// are not. The Footer slot renders directly beneath the submit button, which is what
// makes it adjacent.
// Exported for the same reason `services` is: the slot it goes in decides whether the
// notice lands under the button or back among the fields, and that is the difference
// between a notice adjacent to the action and one buried in the form. (#803)
export const components = {
  // Amplify's sign-in footer is its own "Forgot your password?" button, which runs
  // Cognito's flow. That flow needs a verified address, and signup no longer provides
  // one (#697). Since sign-in stopped saying whether an address has an account (#926),
  // Cognito answers "code sent" for such an account and never sends it. The link to
  // our own flow sits under the form (#698), so this slot is left empty.
  SignIn: {
    Footer() {
      return null;
    },
  },
  SignUp: {
    Footer() {
      return (
        <p className="auth-legal-note">
          By creating an account, you agree to the{' '}
          <a href="/tos" target="_blank" rel="noopener noreferrer">Terms of Service</a>
          {' '}and{' '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
        </p>
      );
    },
  },
};

// Exported so the normalisation can be tested per handler. Testing the helper
// alone would not catch the failure that matters: a handler quietly left unwired.
export const services = {
  // Every Cognito entry point that takes a username is normalised. Covering only
  // some of them is worse than covering none: an account created lowercase but
  // reset from a differently-cased form hits the very wall this removes. (#748)
  async handleSignIn(input: Parameters<typeof signIn>[0]) {
    return signIn(normalizeAuthInput(input));
  },
  async handleConfirmSignUp(input: Parameters<typeof confirmSignUp>[0]) {
    try {
      return await confirmSignUp(normalizeAuthInput(input));
    } catch (e) {
      trackSignupError('email', e);
      throw e;
    }
  },
  async handleResendSignUpCode(input: Parameters<typeof resendSignUpCode>[0]) {
    return resendSignUpCode(normalizeAuthInput(input));
  },
  async handleForgotPassword(input: Parameters<typeof resetPassword>[0]) {
    return resetPassword(normalizeAuthInput(input));
  },
  async handleForgotPasswordSubmit(input: Parameters<typeof confirmResetPassword>[0]) {
    return confirmResetPassword(normalizeAuthInput(input));
  },
  // Wrap the default email sign-up so we can record its failures by name. The Signup
  // conversion itself fires from the first-touch sync (signup.ts), for every method.
  async handleSignUp(input: Parameters<typeof signUp>[0]) {
    // autoSignIn matters more than it looks. Without it, entering the emailed code
    // leaves the user with no session and drops them back on an empty Create Account
    // form — which reads as "that didn't work" at the exact moment they finished
    // converting. With it they land signed in, and AuthPage's `if (user)` redirect
    // takes them to their bookshelf. (#693)
    const normalized = normalizeAuthInput(input);
    let result: Awaited<ReturnType<typeof signUp>>;
    try {
      result = await signUp({
        ...normalized,
        options: {
          ...normalized.options,
          // Required by SignUpOptions, and the Authenticator always supplies it —
          // the fallback only satisfies the type when options is absent entirely.
          userAttributes: normalized.options?.userAttributes ?? {},
          autoSignIn: true,
        },
      });
    } catch (e) {
      trackSignupError('email', e);
      throw e;
    }
    // The Signup conversion no longer fires here. It fires once per new account from
    // the first-touch sync, which also counts Google and Apple sign-ups (#937).
    return result;
  },
};

// Keyboards, for the four fifths of visitors who arrive inside an embedded webview.
//
// 95 of 117 visitors in a day came in under "Mobile App", nearly all from Instagram.
// Those visitors get no Google or Apple button, correctly: Google answers OAuth from a
// webview with 403 disallowed_useragent and nothing on our side changes that. So they
// type every field by hand on a phone, which makes the keyboard that appears the whole
// experience.
//
// The email field shipped as type="text", which on a phone means no @ key: the visitor
// changes keyboard layout to type their own address. type="email" is the fix.
//
// Capitalisation is NOT a problem here, though an earlier reading of this said it was.
// Amplify's FormField hardcodes autoCapitalize="off" after spreading props, so every
// field already has it and an autocapitalize entry in this config would be dead
// weight implying control we do not have. (#806)
const EMAIL_FIELD = {
  placeholder: 'Enter your email address',
  label: 'Email Address',
  isRequired: true,
  type: 'email',
  autocomplete: 'username',
};

export const formFields = {
  signIn: {
    username: { ...EMAIL_FIELD },
  },
  signUp: {
    name: {
      label: 'Display Name',
      placeholder: 'How should we address you?',
      isRequired: true,
      order: 1,
      autocomplete: 'name',
    },
    username: { ...EMAIL_FIELD, order: 2 },
    password: {
      label: 'Password',
      placeholder: 'Enter your password',
      isRequired: true,
      order: 3,
      autocomplete: 'new-password',
    },
    confirm_password: {
      label: 'Confirm Password',
      placeholder: 'Confirm your password',
      isRequired: true,
      order: 4,
      autocomplete: 'new-password',
    },
  },
  // The step straight after signup, and the same cohort: a six digit code typed on an
  // alphabetic keyboard.
  //
  // inputmode, NOT type="tel". Amplify's FormField branches on the type: 'tel' swaps
  // the whole component for PhoneNumberField, which glues a country dial-code <select>
  // onto the field, and our config supplies no dialCode so that select would default to
  // whatever its first option happens to be. A dial-code dropdown beside a six digit
  // email code is a worse screen than the one this set out to fix.
  //
  // inputmode is not in Amplify's FormFieldOptions type, but it is a global HTML
  // attribute and Amplify spreads unknown props straight onto the input. Verified in a
  // browser rather than assumed: it renders as inputmode on the element. (#806)
  confirmSignUp: {
    confirmation_code: {
      label: 'Confirmation Code',
      placeholder: 'Enter the code we emailed you',
      isRequired: true,
      inputmode: 'numeric',
      autocomplete: 'one-time-code',
    },
  },
};

const AuthPage: React.FC<AuthPageProps> = ({ initialState = 'signIn' }) => {
  // `route`, not `initialState`. initialState is fixed when the component mounts, but
  // Amplify renders Sign In / Create Account TABS, and clicking one changes the form
  // through its state machine without navigating or remounting. Gating on the prop
  // meant the note missed the one person it exists for (landed on /register, tapped
  // Sign In) while showing to someone on /login who tapped Create Account, where it
  // describes buttons they are not looking at. route tracks the form on screen. (#808)
  const { user, route, toSignIn, toSignUp } = useAuthenticator((context) => [context.user, context.route]);

  // The form opens on this page's own tab (#1039). Amplify UI takes initialState once per
  // tab, from the first Authenticator: /login reached in a tab that started on /register
  // (the header's Sign In, or signing out after signing up) showed "Welcome Back" over
  // the Create Account form, and the reverse. Decided once per page, on the first screen
  // the form shows, and only moved when that screen is the other form: a tab the person
  // clicks afterwards stays theirs (#808), and a flow already under way (a code to
  // confirm, an error) is left alone. Keyed on the page, not the mount: App remounts
  // AuthPage between /login and /register, but the decision should not depend on it.
  const openedFor = useRef<string | null>(null);
  useEffect(() => {
    if (openedFor.current === initialState) return;
    if (route === 'idle' || route === 'setup' || route === 'signOut' || route === 'transition') return;
    openedFor.current = initialState;
    if (initialState === 'signIn' && route === 'signUp') toSignIn();
    else if (initialState === 'signUp' && route === 'signIn') toSignUp();
  }, [route, initialState, toSignIn, toSignUp]);

  // The form waits while the machine is still loading or signing out (#1039). Amplify UI
  // takes the form's INIT only once the machine is set up, and the form sends it once:
  // mounted during a sign-out, or in the setup that follows one, its INIT was dropped and
  // the form came up as Amplify's stock one. From 6.15.6 a session that fails to refresh
  // is signed out while the page is still loading, with no user yet, so AuthPage's own
  // redirect does not keep the form away then. A first visit's setup is different: the
  // form must be there to send INIT, so only a setup that follows a sign-out is waited out.
  const [signOutSeen, setSignOutSeen] = useState(false);
  useEffect(() => {
    if (route === 'signOut') setSignOutSeen(true);
    else if (route === 'signIn' || route === 'signUp') setSignOutSeen(false);
  }, [route]);
  const holdForm = route === 'idle' || route === 'signOut' || (signOutSeen && route === 'setup');
  const location = useLocation();
  const [recovered, setRecovered] = useState(false);
  const [redirectError, setRedirectError] = useState<string | null>(null);

  // Google refuses OAuth inside an embedded webview (403 disallowed_useragent), so
  // in one of these the social buttons are guaranteed to fail. Suppress them and
  // explain the way out instead of handing the visitor a broken button.
  // Read once: the Authenticator latches socialProviders at mount, and ignores a later change.
  const [inApp] = useState(() => detectInAppBrowser());
  // Computed once alongside inApp: window.location.href is stable on this page and the
  // scheme is only ever rendered into an href, never navigated to from script.
  const iosBrowserUrl = inApp && !inApp.isAndroid
    ? iosExternalBrowserUrl(inApp.app, window.location.href)
    : null;
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('recovered') === 'true') setRecovered(true);
  }, [location.search]);

  // Where a signed-out visitor was going (#948), from ProtectedRoute. Google and Apple
  // sign-in leave the app and come back to the site's root, so it waits in the tab's
  // session storage for App to finish the trip.
  const from = (location.state as { from?: { pathname?: string; search?: string } } | null)?.from;
  const fromPath = from?.pathname ? from.pathname + (from.search || '') : null;
  useEffect(() => {
    if (fromPath) rememberReturnTo(fromPath);
  }, [fromPath]);
  const afterSignIn = safeReturnPath(fromPath) ?? peekReturnTo() ?? '/books';

  // A PreSignUp trigger that throws on the federated path — "an account already
  // exists for this email" — comes back through the OAuth redirect as a Hub event,
  // not as anything the Authenticator renders. Without this the user is bounced to
  // a blank sign-in page with no idea why Google refused them. The event fires on the
  // site's root, where the redirect lands, so it is heard at start-up and handed on
  // here, counted already. (#941)
  useEffect(() => onRedirectFailure(setRedirectError), []);

  if (user) {
    return <Navigate to={afterSignIn} replace />;
  }

  return (
    <div className="auth-page-container container">
      <div className="auth-header-section">
        <img src="/chaptery-mark.svg" alt="Chaptery Logo" className="auth-logo" />
        <h1>{initialState === 'signUp' ? 'Join Chaptery' : 'Welcome Back'}</h1>
        <p className="subtitle">
          {initialState === 'signUp'
            ? 'Start your novel approach to organization today.'
            : 'Sign in to access your cloud-connected tools.'}
        </p>
      </div>

      {recovered && (
        <div className="feedback-banner feedback-success" style={{ marginBottom: '1rem' }}>
          Password reset successfully. Please sign in with your new password.
        </div>
      )}

      {redirectError && (
        <div className="feedback-banner feedback-error" role="alert" style={{ marginBottom: '1rem' }}>
          {redirectError}
        </div>
      )}

      {/* No WARNING while the sign-up form is on screen, on purpose. (The form does now
          offer the way out to Google and Apple, rendered below: that is a choice, #937.)

          This used to be a yellow alert box shown on both routes, explaining that the
          app opens links in its own browser and that Google blocks sign-in there. To
          someone who has just tapped an ad it reads as a warning: talk of browsers
          being blocked, directly above a form asking for a password, on a site they
          have never seen. It also explains an absence nobody perceives, because
          socialProviders is already [] in a webview, so a first-time visitor never
          sees a Google or Apple button to miss. 81% of arrivals are in this webview,
          so it was the first impression for four fifths of the traffic.

          It follows the FORM rather than the URL, so somebody who lands on /register
          and taps the Sign In tab gets it too. That is where the dead end actually
          is, and gating on the route alone would have missed them. (#808)

          #1106 reversed that for sign-up: a plain note above the form now says email
          is the way in (InAppSignUpNote, below), because the old button at the top
          could silently fail and nothing said why. A note, not a warning: it says
          what works here rather than what is blocked. */}
      {inApp && route === 'signIn' && (
        <p className="inapp-signin-note">
          {/* Kept on the sign-in route, where it answers a real dead end rather than
              inventing one: somebody who signed up with Google elsewhere sees only
              email and password here, types their address, and gets "incorrect
              username or password" with no way to work out why. They already have an
              account, so there is nothing to scare them off. */}
          Signed up with Google or Apple?{' '}
          {inApp.isAndroid ? (
            <a href={androidChromeUrl(window.location.href)}>Open this page in Chrome</a>
          ) : iosBrowserUrl ? (
            <a href={iosBrowserUrl}>Open this page in your browser</a>
          ) : (
            // "your browser", never "Safari": the app's menu opens the default browser,
            // which can be Chrome (owner, #937; #1108).
            <>Open this page in your browser</>
          )}
          {' '}to use those buttons. They cannot work inside {inApp.app}.
        </p>
      )}

      {/* Inside an in-app browser, email is the way in, said plainly above the form
          (#1106). Follows the FORM, like the sign-in note above, because Amplify's tabs
          change it without navigating (#808). The browser hand-off is under the form. */}
      {inApp && route === 'signUp' && <InAppSignUpNote inApp={inApp} />}

      <div className="auth-wrapper">
        {/* Not while the machine is loading or signing out: see holdForm. (#1039) */}
        {!holdForm && (
        <Authenticator
          initialState={initialState}
          formFields={formFields}
          components={components}
          services={services}
          socialProviders={inApp ? [] : ['google', 'apple']}
        >
          {/* Amplify runs this only once someone is authenticated, so the branch
              below was unreachable — which is why the legal notice it used to hold
              never appeared on the page at all. It now lives in SignUp.Footer, where
              it renders under the button. (#803) */}
          {/* An empty fragment rather than null: Amplify types the render prop as
              returning an Element. */}
          {({ user: signedInUser }) => signedInUser ? <Navigate to={afterSignIn} replace /> : <></>}
        </Authenticator>
        )}
      </div>

      {/* Under the form, iPhone only: the menu step, if the note's link out fails (#1106, #1111). */}
      {inApp && route === 'signUp' && <InAppSignUpEscape inApp={inApp} />}

      <div className="auth-bottom-links">
        {/* For someone who has an account, so on the Sign In form only, never on Create
            Account (#1105). Follows the FORM, like the notes above. */}
        {route === 'signIn' && (
          <>
            {/* Our own flow rather than Amplify's. Cognito's forgot-password needs a
                verified address and signup no longer provides one (#697), so the stock
                flow fails with a raw error for exactly the people most likely to need
                it. Ours mails the code itself and works either way. (#698) */}
            <Link to="/reset-password">Forgot your password?</Link>
            <span className="separator">|</span>
            <Link to="/recover">Can't access your email? Use your recovery key</Link>
            <span className="separator">|</span>
          </>
        )}
        <Link to="/">Back to Home</Link>
        <span className="separator">|</span>
        <Link to="/pricing">View Pricing</Link>
      </div>
    </div>
  );
};

export default AuthPage;
